import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { CodeDefinition } from '../../../../js/api';
import { RoleEnum } from '../../../../js/api';
import { Button } from '../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components/dialog';
import { DropdownSelect } from '../../components/dropdown-select';
import { Fieldset, Field, Label } from '../../components/fieldset';
import { Input } from '../../components/input';

// Validation schema for phone number
const phoneValidationSchema = z.object({
  phoneNumber: z.string().superRefine((val, ctx) => {
    if (val.length === 0) return; // Allow blank values
    // Basic phone number validation (allows common formats)
    const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    if (!phoneRegex.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid phone number',
        fatal: true,
      });
    }
  }),
});

// Validation message component
const ValidationMessage = ({
  message,
  icon,
  colorClass,
}: {
  message?: string;
  icon?: React.ReactNode;
  colorClass?: string;
}) => {
  if (!message) return null;
  return (
    <div className={`tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-sm ${colorClass}`}>
      {icon}
      {message}
    </div>
  );
};

interface EditFacilityContactDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (contact: {
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    phone_number_extension?: string;
    role?: RoleEnum;
  }) => void;
  roleOptions: CodeDefinition[];
  contact?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
    phone_number_extension?: string;
    role?: RoleEnum;
  };
}

export const EditFacilityContactDialog: React.FC<EditFacilityContactDialogProps> = ({
  open,
  onClose,
  onSave,
  roleOptions,
  contact,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneExtension, setPhoneExtension] = useState('');
  const [role, setRole] = useState<RoleEnum | ''>('');
  const [validationMessages, setValidationMessages] = useState<
    Record<string, { message: string; icon: React.ReactNode; colorClass: string }>
  >({});

  useEffect(() => {
    if (contact) {
      setFirstName(contact.first_name || '');
      setLastName(contact.last_name || '');
      setEmail(contact.email || '');
      setPhoneNumber(contact.phone_number || '');
      setPhoneExtension(contact.phone_number_extension || '');
      setRole(contact.role || '');
    }
  }, [contact, open]);

  // Add validation effect for phone number
  useEffect(() => {
    const result = phoneValidationSchema.safeParse({
      phoneNumber,
    });

    if (!result.success) {
      const error = result.error.errors[0];
      setValidationMessages({
        phoneNumber: {
          message: error.message,
          icon: <XCircleIcon className="tw:h-5 tw:w-5" />,
          colorClass: 'tw:text-stoplight-danger tw:dark:text-red-500',
        },
      });
    } else {
      setValidationMessages({});
    }
  }, [phoneNumber]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Validate phone number before submission
    const result = phoneValidationSchema.safeParse({
      phoneNumber,
    });

    if (!result.success) {
      toast.error('Please fix the validation errors before submitting.');
      return;
    }

    if (!role) {
      toast.error('Role is required.');
      return;
    }

    onSave({
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      phone_number_extension: phoneExtension,
      role: role || undefined,
    });
  };

  const hasValidationErrors = Object.keys(validationMessages).length > 0 || !role;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle className="tw:dark:!text-white/90">Edit Facility Contact</DialogTitle>
      <DialogBody>
        <form onSubmit={handleSubmit} className="tw:space-y-6">
          <Fieldset>
            <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-4">
              <Field>
                <Label htmlFor="first-name">
                  First Name <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input
                  id="first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="tw:w-full"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="last-name">
                  Last Name <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input
                  id="last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="tw:w-full"
                  required
                />
              </Field>
            </div>

            <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-4">
              <Field>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="tw:w-full"
                />
                <ValidationMessage
                  message={validationMessages?.phoneNumber?.message}
                  icon={validationMessages?.phoneNumber?.icon}
                  colorClass={validationMessages?.phoneNumber?.colorClass}
                />
              </Field>
              <Field>
                <Label htmlFor="phone-extension">Phone Extension</Label>
                <Input
                  id="phone-extension"
                  type="tel"
                  value={phoneExtension}
                  onChange={(e) => setPhoneExtension(e.target.value)}
                  className="tw:w-full"
                />
              </Field>
            </div>

            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              <Field>
                <Label htmlFor="email" className="tw:!flex tw:items-center">
                  Email
                  <Popover className="tw:flex tw:items-center tw:relative tw:ml-1">
                    <PopoverButton className="tw:inline-flex tw:items-center tw:justify-center tw:h-5 tw:w-5 tw:focus:outline-none">
                      <i className="far fa-info-circle tw-h-5 tw-w-5 tw-text-zinc-500 tw:hover:text-zinc-600" />
                    </PopoverButton>
                    <PopoverPanel className="tw:absolute tw:z-10 tw:w-64 tw:px-4 tw:py-2 tw:left-6 tw:top-[-24px] tw:mt-2 tw:text-sm tw:bg-white tw:border tw:border-zinc-200 tw:rounded-md tw:shadow-[0_0_20px_rgba(0,0,0,0.20)] tw:dark:bg-zinc-600 tw:dark:border-zinc-700 tw:dark:text-white/90">
                      Changing a contact&apos;s email is not allowed.
                    </PopoverPanel>
                  </Popover>
                </Label>
                <Input id="email" type="email" value={email} className="tw:w-full" disabled />
              </Field>
              <Field>
                <Label htmlFor="role">
                  Role <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <DropdownSelect
                  inputId="role"
                  allOptions={roleOptions.map((opt) => ({ value: opt.code, label: opt.title }))}
                  value={role}
                  onChange={(option) => {
                    if (!option || Array.isArray(option)) {
                      setRole('');
                    } else {
                      setRole(option.value as RoleEnum);
                    }
                  }}
                />
              </Field>
            </div>
          </Fieldset>
        </form>
      </DialogBody>
      <DialogActions>
        <Button type="button" plain onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          color="brandgreen0"
          onClick={handleSubmit}
          disabled={hasValidationErrors}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
